import { PayableReceivablesTypes } from 'libs/feature-special-servicing/src/specialServicing/types/types';

export interface PaReconciliationLoanInfo {
  loanId: number;
  loanStatus: string;
  dealName: string;
  payoffDate: string; // MM/dd/yyyy
}

// The 12 money columns shared by the summary rows and the totals row
export const PA_CURRENCY_FIELDS = [
  'originalAmount',
  'vendorAmountPaid',
  'investorAmountPaid',
  'investorAmountReimb',
  'borrowerPaidPaAmount',
  'borrowerPaidFeeAmount',
  'netPaBalance',
  'dueToFromVendor',
  'dueToFromInvestor',
  'writeoffRch',
  'dueToBorrowerRefund',
  'adjustedPaBalance',
] as const;

export type PaCurrencyField = typeof PA_CURRENCY_FIELDS[number];

export type PaReconciliationTotals = Record<PaCurrencyField, number>;

export interface PaReconciliationRecord extends PaReconciliationTotals {
  recordId: number | null;
  entity: string;
  invoice: string;
  type: string; // fee type (free text from the backend)
  documentId: number | null;
  transactionNumber: string | null;
  invoiceDate: string | null;
  investor: string | null;
  payableStatus: PayableReceivablesTypes | null;
  payableAdjustment: number;
  vendorPaidDate: string | null;
  receivableStatus: PayableReceivablesTypes | null;
  receivableAmount: number;
  receivablePaidDate: string | null;
  vendorPaymentDate: string | null;
  investorPaidDate: string | null;
  investorReimbDate: string | null;
  borrowerPaidDate: string | null;
  comments: string | null;
}

export interface PaReconciliationSummaryRow extends PaReconciliationTotals {
  type: string;
  recordCount: number;
}

export interface PaReconciliationData {
  loanInfo: PaReconciliationLoanInfo;
  records: PaReconciliationRecord[];
}

export const roundCurrency = (value: number) => Math.round((value + Number.EPSILON) * 100) / 100;

export const sumCurrencyFields = (rows: PaReconciliationTotals[]): PaReconciliationTotals => {
  const totals = {} as PaReconciliationTotals;
  PA_CURRENCY_FIELDS.forEach(field => {
    totals[field] = roundCurrency(rows.reduce((acc, row) => acc + (row[field] ?? 0), 0));
  });
  return totals;
};

export const recalculateRecord = (record: PaReconciliationRecord): PaReconciliationRecord => {
  const netPaBalance = roundCurrency(
    record.vendorAmountPaid + record.investorAmountPaid + record.investorAmountReimb + record.borrowerPaidPaAmount
  );
  const adjustedPaBalance = roundCurrency(
    netPaBalance + record.dueToFromVendor + record.writeoffRch + record.dueToBorrowerRefund
  );
  return { ...record, netPaBalance, adjustedPaBalance };
};
